import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { withStyles, Typography, Popover } from '@material-ui/core'
import { ChromePicker } from 'react-color'
import { useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { CoreButton, CoreIconButton } from 'src/components/core'
import styles from './styles'

// Hotspot utilities
import {
  handleHotspotBackgroundColorChange,
  handleHotspotIconColorChange,
  handleHotspotIconSelect,
  handleHotspotIconDelete,
  showHotspotIconSelector,
  deleteHotspot,
  getInitialHotspotState,
} from '../utils/hotspotUtils'

const HotspotSettingsModal = props => {
  const { classes, editor, target, selection, position } = props
  const dispatch = useDispatch()

  const [backgroundColor, setBackgroundColor] = useState('#007cba')
  const [iconColor, setIconColor] = useState('#ffffff')
  const [selectedIcon, setSelectedIcon] = useState('')
  const [selectedIconName, setSelectedIconName] = useState('mdi:plus')
  const [hotspotId, setHotspotId] = useState('')

  const [backgroundColorAnchorEl, setBackgroundColorAnchorEl] = useState(null)
  const [iconColorAnchorEl, setIconColorAnchorEl] = useState(null)
  const [pickerVisibles, setPickerVisibles] = useState({
    backgroundColor: false,
    iconColor: false,
  })

  useEffect(() => {
    if (selection) {
      const initialState = getInitialHotspotState(selection)
      setBackgroundColor(initialState.backgroundColor)
      setIconColor(initialState.iconColor)
      setSelectedIcon(initialState.selectedIcon)
      setSelectedIconName(initialState.selectedIconName)
      setHotspotId(initialState.hotspotId)
    }
  }, [selection])

  const onBackgroundColorChange = event => {
    const newColor = event.hex
    setBackgroundColor(newColor)
    handleHotspotBackgroundColorChange(newColor, {
      editor,
      hotspotId,
      iconColor,
      selectedIcon,
      selectedIconName,
    })
  }

  const onIconColorChange = event => {
    const newColor = event.hex
    setIconColor(newColor)
    handleHotspotIconColorChange(newColor, {
      editor,
      hotspotId,
      backgroundColor,
      selectedIcon,
      selectedIconName,
    })
  }

  const onIconSelect = icon => {
    handleHotspotIconSelect(icon, {
      editor,
      hotspotId,
      backgroundColor,
      iconColor,
      setSelectedIcon,
      setSelectedIconName,
    })
  }

  const onIconDelete = () => {
    handleHotspotIconDelete({
      editor,
      hotspotId,
      backgroundColor,
      iconColor,
      setSelectedIcon,
      setSelectedIconName,
    })
  }

  const onShowIconSelector = () => {
    showHotspotIconSelector({
      editor,
      target,
      handleIconSelect: onIconSelect,
      dispatch,
    })
  }

  const onDeleteClick = () => {
    deleteHotspot(editor, hotspotId)
    dispatch(messageBoxHideAction())
  }

  return (
    <div className={classes.box}>
      {/* Hotspot Style Section */}
      <div className={classes.row}>
        {/* Button Color Section */}
        <div>
          <Typography className={classes.message}>Button Color</Typography>
        </div>
        <div className={classes.boxIconButton}>
          <div className={classes.inputItem}>
            <div className={classes.pickerBg}>
              <span
                className={classes.pickerSpan}
                style={{
                  backgroundColor: backgroundColor,
                  zIndex: '999999 !important',
                }}
                onClick={event => {
                  setPickerVisibles({
                    ...pickerVisibles,
                    backgroundColor: !pickerVisibles.backgroundColor,
                  })
                  setBackgroundColorAnchorEl(event.currentTarget)
                }}
              ></span>
              <Popover
                className={classes.popover}
                id={
                  pickerVisibles.backgroundColor ? 'backgroundColor' : undefined
                }
                open={pickerVisibles.backgroundColor}
                anchorEl={backgroundColorAnchorEl}
                onClose={() => {
                  setPickerVisibles({
                    ...pickerVisibles,
                    backgroundColor: false,
                  })
                  setBackgroundColorAnchorEl(null)
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker
                  color={backgroundColor}
                  onChange={onBackgroundColorChange}
                />
              </Popover>
            </div>
          </div>
          <Typography className={classes.header}>{backgroundColor}</Typography>
        </div>

        {/* Icon Color Section */}
        <div>
          <Typography className={classes.message}>Icon Color</Typography>
        </div>
        <div className={classes.boxIconButton}>
          <div className={classes.inputItem}>
            <div className={classes.pickerBg}>
              <span
                className={classes.pickerSpan}
                style={{
                  backgroundColor: iconColor,
                  zIndex: '999999 !important',
                }}
                onClick={event => {
                  setPickerVisibles({
                    ...pickerVisibles,
                    iconColor: !pickerVisibles.iconColor,
                  })
                  setIconColorAnchorEl(event.currentTarget)
                }}
              ></span>
              <Popover
                className={classes.popover}
                id={pickerVisibles.iconColor ? 'iconColor' : undefined}
                open={pickerVisibles.iconColor}
                anchorEl={iconColorAnchorEl}
                onClose={() => {
                  setPickerVisibles({
                    ...pickerVisibles,
                    iconColor: false,
                  })
                  setIconColorAnchorEl(null)
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker color={iconColor} onChange={onIconColorChange} />
              </Popover>
            </div>
          </div>
          <Typography className={classes.header}>{iconColor}</Typography>
        </div>

        {/* Icon Section */}
        <div>
          <Typography className={classes.message}>Icon</Typography>
        </div>
        <div className={classes.boxIconButton}>
          {!selectedIcon ? (
            <CoreIconButton
              color="dark"
              size="small"
              variant="text"
              onClick={onShowIconSelector}
              className={classes.iconButtonAdd}
            >
              <Icon icon={selectedIconName} />
            </CoreIconButton>
          ) : (
            <CoreIconButton
              color="dark"
              size="small"
              variant="text"
              onClick={onShowIconSelector}
              className={classes.iconButtonChange}
            >
              <Icon icon={selectedIconName} />
            </CoreIconButton>
          )}
          <Typography className={classes.header}>
            {!selectedIcon ? 'Add' : 'Change'} Icon
          </Typography>
          {selectedIcon && (
            <CoreIconButton
              color="dark"
              onClick={onIconDelete}
              size="small"
              variant="text"
              className={classes.iconButton}
            >
              <Icon className={classes.sourceIcon} icon="iconoir:trash" />
            </CoreIconButton>
          )}
        </div>
      </div>

      {/* Delete Section */}
      <div className={classes.row}>
        <div className={classes.boxDelete}>
          <CoreButton
            variant="text"
            color="error"
            className={classes.deleteButton}
            size="small"
            onClick={onDeleteClick}
          >
            <Icon icon="iconoir:trash" />
            <Typography className={classes.deleteTitle}>
              Delete hotspot
            </Typography>
          </CoreButton>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(HotspotSettingsModal)
